<template>
    <el-dialog v-model="visible" title="确认下载试卷" width="480px" center destroy-on-close :before-close="handleClose"
        class="premium-pay-dialog">
        <div class="pay-container" v-if="payInfo">
            <!-- Header: Resource Info -->
            <div class="resource-header">
                <div class="resource-label">您正在下载：</div>
                <div class="resource-name">{{ paperName }}</div>
            </div>

            <!-- Status: Already Paid -->
            <div class="already-paid-banner" v-if="payInfo.is_paid">
                <el-icon><CircleCheckFilled /></el-icon>
                <span>您已获得该资源，可直接免费下载</span>
            </div>

            <!-- Pricing Section -->
            <div class="pricing-section" v-else>
                <div class="price-row">
                    <span class="label">资源原价</span>
                    <span class="value">¥{{ (payInfo.base_price / 100).toFixed(2) }}</span>
                </div>
                <div class="price-row" v-if="payInfo.promo_discount > 0">
                    <span class="label">活动优惠</span>
                    <span class="value" style="color: #f56c6c;">-¥{{ (payInfo.promo_discount / 100).toFixed(2) }}</span>
                </div>

                <!-- Points Offset -->
                <div class="points-card" v-if="payInfo.max_points_offset > 0">
                    <div class="points-header">
                        <el-checkbox v-model="usePoints" @change="calculateFinalPrice">
                            使用积分抵扣
                        </el-checkbox>
                        <span class="user-balance">可用: {{ userPointsBalance }}</span>
                    </div>
                    <div class="points-body" v-if="usePoints">
                        <span class="offset-val">-¥{{ (Math.min(userPointsBalance, payInfo.max_points_offset) / 100).toFixed(2) }}</span>
                        <span class="offset-label">({{ Math.min(userPointsBalance, payInfo.max_points_offset) }} 积分)</span>
                    </div>
                </div>

                <div class="final-payment" v-if="payInfo.original_price > 0">
                    <span class="label">最终实付</span>
                    <span class="amount">¥{{ (finalPayAmount / 100).toFixed(2) }}</span>
                </div>
            </div>

            <!-- QR Code Section -->
            <div class="qr-container" v-if="orderQR">
                <div class="pay-method-header">
                    <img src="@/assets/weixin_pay_logo.png" class="wx-logo" alt="WeChat Pay" />
                    <span class="pay-text">微信扫码支付</span>
                </div>
                
                <div class="qr-wrapper">
                    <div class="qr-border">
                        <img :src="orderQR" alt="支付二维码" class="qr-img" />
                    </div>
                    <div class="pay-instruction">请使用微信“扫一扫”完成支付</div>
                </div>

                <div class="pay-status-tip">
                    <el-icon class="is-loading"><Loading /></el-icon>
                    <span>支付成功后将自动开始下载，请勿关闭</span>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="dialog-footer">
                <template v-if="!orderQR">
                    <el-button @click="handleClose" round class="premium-btn">取消</el-button>
                    <el-button type="primary" :loading="creatingOrder" @click="handleConfirmDownload" round
                        class="premium-btn confirm-btn">
                        {{ payInfo && payInfo.is_paid ? '立即免费下载' : (finalPayAmount === 0 ? '免费下载' : '确认并去支付') }}
                    </el-button>
                </template>
                <template v-else>
                    <el-button @click="handleClose" round class="premium-btn">关闭窗口</el-button>
                </template>
            </div>
        </template>
    </el-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { CircleCheckFilled, Loading } from '@element-plus/icons-vue'
import api, { getToken } from '@/utils/api'

const props = defineProps({
    modelValue: Boolean,
    paperId: [Number, String],
    paperName: String,
    customPaperData: Object // For Zujuan.vue custom papers
})

const emit = defineEmits(['update:modelValue', 'success'])

const visible = ref(false)
const payInfo = ref(null)
const usePoints = ref(true)
const userPointsBalance = ref(0)
const finalPayAmount = ref(0)
const creatingOrder = ref(false)
const orderQR = ref('')
const currentOrderNo = ref('')
let pollingTimer = null

watch(() => props.modelValue, (val) => {
    visible.value = val
    if (val) {
        fetchPayInfo()
    } else {
        stopPolling()
    }
})

const handleClose = () => {
    emit('update:modelValue', false)
}

const fetchPayInfo = async () => {
    try {
        const params = {}
        if (props.paperId) {
            params.paperId = props.paperId
        }

        if (props.customPaperData) {
            params.resource_type = 'custom_paper'
            params.question_count = props.customPaperData.questions?.length || 0
            params.questions = props.customPaperData.questions?.join(',') || ''
        }

        const res = await api.get('/api/download/price', { params })
        payInfo.value = res.data

        const pointsRes = await api.get('/api/points/balance')
        userPointsBalance.value = pointsRes.data.total_points

        // If already paid or free, auto success
        if (res.data.final_cash_amount === 0 && !res.data.is_paid) {
            emit('success', { downloadToken: '' })
            handleClose()
            return
        }

        calculateFinalPrice()
    } catch (e) {
        ElMessage.error('获取价格信息失败')
        handleClose()
    }
}

const calculateFinalPrice = () => {
    if (!payInfo.value) return
    if (usePoints.value) {
        // Find how many points we can actually use
        const pts = Math.min(userPointsBalance.value, payInfo.value.max_points_offset)
        // In this system 1 point = 1 cent (as per backend config)
        finalPayAmount.value = payInfo.value.original_price - pts
    } else {
        finalPayAmount.value = payInfo.value.original_price
    }
}

const handleConfirmDownload = async () => {
    creatingOrder.value = true
    try {
        const res = await api.post('/api/download/order', {
            paperId: props.paperId,
            usePoints: usePoints.value,
            customPaperData: props.customPaperData
        })

        if (res.data.qr_url) {
            orderQR.value = res.data.qr_url
            currentOrderNo.value = res.data.out_trade_no
            startPollingStatus(res.data.out_trade_no)
        } else if (res.data.download_token) {
            emit('success', { downloadToken: res.data.download_token })
            handleClose()
        }
    } catch (e) {
        ElMessage.error(e.response?.data?.error || '创建订单失败')
    } finally {
        creatingOrder.value = false
    }
}

const startPollingStatus = (orderNo) => {
    pollingTimer = setInterval(async () => {
        try {
            const res = await api.get('/api/download/token', { params: { out_trade_no: orderNo } })
            if (res.data.download_token) {
                ElMessage.success('支付成功！')
                emit('success', { downloadToken: res.data.download_token })
                handleClose()
                stopPolling()
            }
        } catch (e) { }
    }, 3000)

    setTimeout(() => stopPolling(), 600000)
}

const stopPolling = () => {
    if (pollingTimer) {
        clearInterval(pollingTimer)
        pollingTimer = null
    }
}
</script>

<style scoped>
.pay-container {
    padding: 0 4px;
}

.resource-header {
    text-align: center;
    margin-bottom: 24px;
}

.resource-label {
    font-size: 13px;
    color: #909399;
    margin-bottom: 8px;
}

.resource-name {
    font-size: 18px;
    font-weight: 700;
    color: #1d1d1f;
    line-height: 1.4;
    padding: 0 20px;
}

.already-paid-banner {
    background: #f0f9eb;
    border: 1px solid #e1f3d8;
    color: #67c23a;
    padding: 16px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-weight: 600;
}

.pricing-section {
    background: #fdfdfd;
    border-radius: 16px;
    padding: 4px 0;
}

.price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f2f2f1;
}

.price-row .label {
    color: #86868b;
}

.price-row .value {
    font-size: 18px;
    font-weight: 600;
    color: #1d1d1f;
}

.points-card {
    background: linear-gradient(135deg, #fffcf5 0%, #fff7e6 100%);
    border: 1px solid #ffe8cc;
    border-radius: 12px;
    padding: 15px;
    margin: 18px 0;
}

.points-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.user-balance {
    font-size: 11px;
    color: #c28834;
    background: #fff;
    padding: 2px 8px;
    border-radius: 10px;
}

.points-body {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.offset-val {
    font-size: 20px;
    font-weight: 700;
    color: #ff9900;
}

.offset-label {
    font-size: 12px;
    color: #ff9900;
    opacity: 0.8;
}

.final-payment {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
}

.final-payment .label {
    font-weight: 600;
    color: #1d1d1f;
}

.final-payment .amount {
    font-size: 28px;
    font-weight: 800;
    color: #f56c6c;
}

/* QR Code Styling */
.qr-container {
    margin-top: 10px;
    background: #fff;
    border: 1px solid #f2f2f1;
    border-radius: 16px;
    padding: 24px 20px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
}

.pay-method-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
}

.wx-logo {
    height: 24px;
}

.pay-text {
    font-size: 16px;
    font-weight: 700;
    color: #07c160;
}

.qr-wrapper {
    display: inline-block;
    background: #fff;
}

.qr-border {
    padding: 12px;
    background: #fff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.02);
}

.qr-img {
    width: 180px;
    height: 180px;
    display: block;
}

.pay-instruction {
    margin-top: 12px;
    font-size: 13px;
    color: #888;
}

.pay-status-tip {
    margin-top: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #07c160;
    font-size: 13px;
    font-weight: 500;
    background: #f0fdf4;
    padding: 10px;
    border-radius: 8px;
}

.dialog-footer {
    display: flex;
    justify-content: center;
    gap: 15px;
    padding-bottom: 10px;
}

.premium-btn {
    padding: 12px 24px;
    height: auto;
    font-weight: 600;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.confirm-btn {
    padding-left: 30px;
    padding-right: 30px;
}

:deep(.premium-pay-dialog) {
    border-radius: 20px;
    overflow: hidden;
}

:deep(.premium-pay-dialog .el-dialog__header) {
    padding-top: 25px;
    margin-right: 0;
}

:deep(.premium-pay-dialog .el-dialog__title) {
    font-weight: 700;
    color: #1d1d1f;
}
</style>
